import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Bell, Plus, AlertTriangle, AlertCircle, CheckCircle2, Clock, Trash2, Pencil, RefreshCcw, Sparkles, Calendar, Settings, FileDown, CalendarRange } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useBranchStore } from '@/stores';
import { localToday, toLocalYmd } from '@/lib/dateRange';

const STATUS_META = {
  overdue:  { icon: AlertTriangle, label: 'Overdue',     class: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 border-rose-300', tile: 'border-rose-300 bg-rose-50 dark:bg-rose-950/20' },
  due_soon: { icon: AlertCircle,   label: 'Due Soon',    class: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-300', tile: 'border-amber-300 bg-amber-50 dark:bg-amber-950/20' },
  upcoming: { icon: Clock,         label: 'Upcoming',    class: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-300', tile: 'border-blue-300 bg-blue-50 dark:bg-blue-950/20' },
  ok:       { icon: CheckCircle2,  label: 'OK',          class: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-300', tile: 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20' },
};

const EMPTY_FORM = {
  title: '', reminder_type: 'commercial_registration',
  issued_date: '', expiry_date: '', advance_days: 30,
  branch_id: '', reference_number: '', issuing_authority: '',
  responsible_person: '', notes: '', is_active: true,
};

export default function ComplianceRemindersPage() {
  const { branches, fetchBranches } = useBranchStore();
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [dashboard, setDashboard] = useState({ counts: {}, total_active: 0 });
  const [statusFilter, setStatusFilter] = useState('all');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [renewOpen, setRenewOpen] = useState(false);
  const [renewing, setRenewing] = useState(null);
  const [renewForm, setRenewForm] = useState({ new_expiry_date: '', reference_number: '', note: '' });

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({ alert_mode: 'per_item', healthy_at: 85, warning_at: 60 });

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarData, setCalendarData] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);

  const loadCalendar = async () => {
    setCalendarLoading(true);
    try {
      const r = await api.get('/compliance/renewal-calendar?horizon_days=180');
      setCalendarData(r.data);
    } catch { toast.error('Failed to load calendar'); }
    finally { setCalendarLoading(false); }
  };
  useEffect(() => { if (calendarOpen && !calendarData) loadCalendar(); }, [calendarOpen]);

  const loadSettings = async () => {
    try {
      const r = await api.get('/compliance/settings');
      setSettings(r.data);
    } catch { /* silent */ }
  };
  const saveSettings = async () => {
    if (settings.warning_at >= settings.healthy_at) {
      toast.error('Warning threshold must be lower than Healthy threshold');
      return;
    }
    try {
      await api.put('/compliance/settings', settings);
      toast.success('Compliance settings saved');
      setSettingsOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const fetchAll = async () => {
    try {
      const [list, dash, t] = await Promise.all([
        api.get('/compliance/reminders'),
        api.get('/compliance/reminders/dashboard'),
        api.get('/compliance/types'),
      ]);
      setItems(list.data.items || []);
      setDashboard(dash.data || { counts: {}, total_active: 0 });
      setTypes(t.data || []);
    } catch { toast.error('Failed to load compliance data'); }
  };

  useEffect(() => {
    fetchBranches();
    fetchAll();
    loadSettings();
  }, []);

  const seedDefaults = async () => {
    try {
      const res = await api.post('/compliance/reminders/seed-defaults');
      toast.success(res.data.created > 0
        ? `Seeded ${res.data.created} default reminder template(s) — please update with your real expiry dates`
        : res.data.message);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const save = async () => {
    if (!form.title || !form.expiry_date) { toast.error('Title + expiry date required'); return; }
    try {
      if (editing) await api.put(`/compliance/reminders/${editing.id}`, form);
      else         await api.post('/compliance/reminders', form);
      toast.success(editing ? 'Updated' : 'Created');
      setFormOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const startEdit = (r) => {
    setEditing(r);
    setForm({
      title: r.title, reminder_type: r.reminder_type, issued_date: r.issued_date || '',
      expiry_date: r.expiry_date || '', advance_days: r.advance_days || 30,
      branch_id: r.branch_id || '', reference_number: r.reference_number || '',
      issuing_authority: r.issuing_authority || '', responsible_person: r.responsible_person || '',
      notes: r.notes || '', is_active: r.is_active !== false,
    });
    setFormOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this reminder?')) return;
    try { await api.delete(`/compliance/reminders/${id}`); toast.success('Deleted'); fetchAll(); }
    catch { toast.error('Delete failed'); }
  };

  const startRenew = (r) => {
    setRenewing(r);
    // Default new expiry = current expiry + 1 year
    const cur = new Date(r.expiry_date);
    cur.setFullYear(cur.getFullYear() + 1);
    setRenewForm({ new_expiry_date: toLocalYmd(cur), reference_number: r.reference_number || '', note: '' });
    setRenewOpen(true);
  };

  const doRenew = async () => {
    try {
      await api.post(`/compliance/reminders/${renewing.id}/renew`, renewForm);
      toast.success('Renewed');
      setRenewOpen(false);
      setRenewing(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const filteredItems = statusFilter === 'all' ? items : items.filter(i => i.status === statusFilter);
  const branchName = (id) => branches?.find(b => b.id === id)?.name || '';

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-7xl mx-auto" data-testid="compliance-reminders-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
              <Bell size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Compliance Reminders</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Track government licenses, certificates, and any document with an expiry date — get alerts before they lapse
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} data-testid="compliance-settings-btn">
              <Settings size={12} className="mr-1" /> Settings
            </Button>
            <Button variant="outline" size="sm" data-testid="audit-trail-pdf-btn"
              onClick={async () => {
                try {
                  const r = await api.get('/compliance/audit-trail-pdf?days=90', { responseType: 'blob' });
                  const url = URL.createObjectURL(r.data);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `compliance-audit-trail-${localToday()}.pdf`;
                  a.click();
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                  toast.success('Audit trail downloaded');
                } catch { toast.error('Failed to generate PDF'); }
              }}>
              <FileDown size={12} className="mr-1" /> Audit PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCalendarOpen(true)} data-testid="renewal-calendar-btn">
              <CalendarRange size={12} className="mr-1" /> Renewal Calendar
            </Button>
            <Button variant="outline" size="sm" onClick={seedDefaults} data-testid="seed-defaults-btn">
              <Sparkles size={12} className="mr-1" /> Seed defaults
            </Button>
            <Button onClick={() => { setEditing(null); setForm(EMPTY_FORM); setFormOpen(true); }}
              data-testid="add-reminder-btn"
              className="bg-amber-500 hover:bg-amber-600 text-white">
              <Plus size={14} className="mr-1" /> Add Reminder
            </Button>
          </div>
        </div>

        {/* Status tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['overdue', 'due_soon', 'upcoming', 'ok'].map(k => {
            const meta = STATUS_META[k];
            const Icon = meta.icon;
            const count = dashboard.counts?.[k] || 0;
            return (
              <Card key={k} className={`cursor-pointer border-2 transition-all ${meta.tile} ${statusFilter === k ? 'ring-2 ring-offset-1 ring-foreground' : ''}`}
                onClick={() => setStatusFilter(statusFilter === k ? 'all' : k)}
                data-testid={`status-tile-${k}`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center justify-between">
                    <Icon size={22} />
                    <span className="text-3xl font-bold font-outfit">{count}</span>
                  </div>
                  <p className="text-xs uppercase font-bold mt-1 tracking-wider">{meta.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Calendar size={14}/> Reminders {statusFilter !== 'all' ? `(${STATUS_META[statusFilter].label})` : ''}</CardTitle>
            {statusFilter !== 'all' && (
              <Button size="sm" variant="ghost" onClick={() => setStatusFilter('all')}>Clear filter</Button>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b border-border text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2">Title / Type</th>
                    <th className="text-left px-4 py-2">Authority</th>
                    <th className="text-left px-4 py-2">Branch</th>
                    <th className="text-left px-4 py-2">Reference #</th>
                    <th className="text-left px-4 py-2">Expiry</th>
                    <th className="text-left px-4 py-2">Days Left</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-right px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">
                      No reminders. Click "Seed defaults" for common KSA templates or "Add Reminder" for a custom one.
                    </td></tr>
                  ) : filteredItems.map(r => {
                    const meta = STATUS_META[r.status] || STATUS_META.ok;
                    const Icon = meta.icon;
                    return (
                      <tr key={r.id} className="border-b border-border hover:bg-muted/30" data-testid={`reminder-${r.id}`}>
                        <td className="px-4 py-2">
                          <div className="font-medium">{r.title}</div>
                          <div className="text-[10px] text-muted-foreground uppercase">{r.reminder_type}</div>
                        </td>
                        <td className="px-4 py-2 text-xs">{r.issuing_authority || '—'}</td>
                        <td className="px-4 py-2 text-xs">{branchName(r.branch_id) || '—'}</td>
                        <td className="px-4 py-2 text-xs font-mono">{r.reference_number || '—'}</td>
                        <td className="px-4 py-2 text-xs font-mono">{r.expiry_date}</td>
                        <td className="px-4 py-2 text-xs font-mono font-semibold">
                          {r.days_remaining !== null
                            ? r.days_remaining < 0
                              ? <span className="text-rose-600">{Math.abs(r.days_remaining)}d ago</span>
                              : <span className={r.days_remaining <= 7 ? 'text-amber-600' : 'text-foreground'}>{r.days_remaining}d</span>
                            : '—'}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${meta.class}`}>
                            <Icon size={11} /> {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button onClick={() => startRenew(r)} className="px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 text-xs font-medium dark:bg-emerald-900/30 dark:text-emerald-400" title="Renew" data-testid={`renew-${r.id}`}>
                              <RefreshCcw size={11} className="inline" /> Renew
                            </button>
                            <button onClick={() => startEdit(r)} className="px-2 py-1 rounded hover:bg-muted text-xs"><Pencil size={11} /></button>
                            <button onClick={() => remove(r.id)} className="px-2 py-1 rounded hover:bg-error/10 text-error text-xs"><Trash2 size={11} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Add/Edit dialog */}
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? 'Edit Reminder' : 'Add Reminder'}</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Saudi CR Renewal" data-testid="reminder-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={form.reminder_type} onValueChange={(v) => setForm({ ...form, reminder_type: v })}>
                    <SelectTrigger data-testid="reminder-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {types.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Branch</Label>
                  <Select value={form.branch_id || 'none'} onValueChange={(v) => setForm({ ...form, branch_id: v === 'none' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="Tenant-wide" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Tenant-wide (no branch)</SelectItem>
                      {(branches || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Issued Date</Label>
                  <Input type="date" value={form.issued_date} onChange={(e) => setForm({ ...form, issued_date: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Expiry Date *</Label>
                  <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                    data-testid="reminder-expiry" />
                </div>
                <div>
                  <Label className="text-xs">Alert Before</Label>
                  <Select value={String(form.advance_days)} onValueChange={(v) => setForm({ ...form, advance_days: parseInt(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[7, 14, 30, 60, 90].map(d => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Reference / License #</Label>
                  <Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Issuing Authority</Label>
                  <Input value={form.issuing_authority} onChange={(e) => setForm({ ...form, issuing_authority: e.target.value })}
                    placeholder="ZATCA, Chamber, Baladiya..." />
                </div>
              </div>
              <div>
                <Label className="text-xs">Responsible Person</Label>
                <Input value={form.responsible_person} onChange={(e) => setForm({ ...form, responsible_person: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="save-reminder-btn"
                className="bg-amber-500 hover:bg-amber-600 text-white">{editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Renew dialog */}
        <Dialog open={renewOpen} onOpenChange={setRenewOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Renew: {renewing?.title}</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">New Expiry Date *</Label>
                <Input type="date" value={renewForm.new_expiry_date}
                  onChange={(e) => setRenewForm({ ...renewForm, new_expiry_date: e.target.value })}
                  data-testid="renew-expiry-input" />
              </div>
              <div>
                <Label className="text-xs">New Reference / License #</Label>
                <Input value={renewForm.reference_number}
                  onChange={(e) => setRenewForm({ ...renewForm, reference_number: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Note</Label>
                <Input value={renewForm.note} onChange={(e) => setRenewForm({ ...renewForm, note: e.target.value })} />
              </div>
              {renewing?.renewal_history?.length > 0 && (
                <div className="text-xs space-y-1 mt-2 border-t pt-2">
                  <p className="font-semibold text-muted-foreground">Renewal history:</p>
                  {renewing.renewal_history.slice(-3).map((h, i) => (
                    <p key={i} className="text-muted-foreground font-mono">
                      {(h.renewed_at || '').slice(0, 10)}: {h.previous_expiry} → {h.new_expiry} ({h.renewed_by})
                    </p>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenewOpen(false)}>Cancel</Button>
              <Button onClick={doRenew} className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="confirm-renew-btn">Renew</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Settings dialog */}
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogContent className="max-w-md" data-testid="compliance-settings-dialog">
            <DialogHeader><DialogTitle>Compliance Settings</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2 text-sm">
              <div>
                <Label className="text-xs">Email Alert Mode</Label>
                <Select value={settings.alert_mode} onValueChange={(v) => setSettings({ ...settings, alert_mode: v })}>
                  <SelectTrigger data-testid="alert-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_item">Per-item — one email per overdue / due-soon row</SelectItem>
                    <SelectItem value="daily_digest">Daily digest — one summary email per day</SelectItem>
                    <SelectItem value="off">In-app only — no emails</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Switch to <strong>daily_digest</strong> to receive a single rolled-up email each morning instead of one per alert.
                </p>
              </div>
              <div className="border-t pt-3">
                <Label className="text-xs">Compliance Health Score thresholds</Label>
                <p className="text-[11px] text-muted-foreground mb-2">
                  Override the default 85 / 60 thresholds used by the dashboard score dial.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase">Healthy at ≥</Label>
                    <Input type="number" min="0" max="100" value={settings.healthy_at}
                      onChange={(e) => setSettings({ ...settings, healthy_at: parseInt(e.target.value) || 0 })}
                      data-testid="healthy-at-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase">Warning at ≥</Label>
                    <Input type="number" min="0" max="100" value={settings.warning_at}
                      onChange={(e) => setSettings({ ...settings, warning_at: parseInt(e.target.value) || 0 })}
                      data-testid="warning-at-input" />
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Score below the warning threshold is shown as <strong>critical</strong>.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
              <Button onClick={saveSettings} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="save-compliance-settings">
                Save Settings
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Renewal Calendar dialog */}
        <Dialog open={calendarOpen} onOpenChange={setCalendarOpen}>
          <DialogContent className="max-w-3xl max-h-[85dvh] overflow-y-auto" data-testid="renewal-calendar-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarRange size={16} className="text-violet-600" />
                Suggested Renewal Calendar — next 180 days
              </DialogTitle>
            </DialogHeader>
            <div className="py-2 text-sm">
              {calendarLoading ? (
                <div className="py-8 text-center text-muted-foreground">Loading…</div>
              ) : calendarData ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                    <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700">Start now</div>
                      <div className="text-2xl font-bold text-rose-700 dark:text-rose-400">{calendarData.start_now_count}</div>
                      <p className="text-[10px] text-muted-foreground">Items past their lead time</p>
                    </div>
                    <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">In horizon</div>
                      <div className="text-2xl font-bold text-amber-700 dark:text-amber-400">{calendarData.items.length}</div>
                      <p className="text-[10px] text-muted-foreground">Total items requiring action</p>
                    </div>
                    <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-blue-700">Weeks active</div>
                      <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">{calendarData.weeks.length}</div>
                      <p className="text-[10px] text-muted-foreground">ISO weeks with renewals to start</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {calendarData.weeks.length === 0 ? (
                      <p className="text-center py-6 text-muted-foreground">No renewals to schedule in the horizon. Healthy pipeline 🟢</p>
                    ) : calendarData.weeks.map(wk => (
                      <div key={wk.week} className="border border-border rounded-lg overflow-hidden">
                        <div className="px-3 py-2 bg-muted/50 text-xs font-semibold text-foreground inline-flex items-center justify-between w-full">
                          <span>Week {wk.week}</span>
                          <span className="text-muted-foreground">{wk.items.length} item(s)</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <tbody>
                              {wk.items.map((it, i) => (
                                <tr key={i} className="border-t border-border">
                                  <td className="px-3 py-1.5 font-medium">{it.title}</td>
                                  <td className="px-2 py-1.5 font-mono text-muted-foreground">expires {it.expiry}</td>
                                  <td className="px-2 py-1.5 font-mono text-violet-600 dark:text-violet-400">start {it.suggested_start}</td>
                                  <td className={`px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider font-bold ${it.days_until_start <= 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                    {it.days_until_start <= 0 ? `${-it.days_until_start}d overdue` : `in ${it.days_until_start}d`}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-3 italic">
                    Heuristic-based: visa/iqama/passport → 60-day lead, CR/VAT/license → 45-day, civil-defense/municipality → 30-day, others → 21-day.
                  </p>
                </>
              ) : (
                <div className="py-8 text-center text-muted-foreground">Click "Renewal Calendar" to load</div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
